import useReturnRequestsRatesTimeChart from "@babackapp/private-api-client/client/use-return-requests-rates-time-chart";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@babackapp/ui/components/chart";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@babackapp/ui/components/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/ui/components/tooltip";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { format } from "date-fns";
import { formatCountryList } from "@/utils/country";
import { useLocale } from "@babackapp/translations/use-set-locale";
import { useMemo, useState } from "react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { useAnalyticsFilters } from "../hooks/use-analytics-filters";
import { useDebouncedLoading } from "../hooks/use-debounced-loading";
import type { ChartSmoothing } from "../providers/analytics-filters.provider";
import {
  RETURN_RATE_COLORS,
  RETURN_RATE_THRESHOLDS,
} from "../utils/return-rate-colors";

/**
 * Aggregate hourly data into daily buckets
 */
function aggregateByDay(
  data: {
    orderedAt: number;
    returned: number;
    ordered: number;
    countryOrdered?: number;
  }[],
): ChartDataPoint[] {
  const dayMs = 24 * 60 * 60 * 1000;
  const dailyMap = new Map<
    number,
    { returned: number; ordered: number; countryOrdered: number }
  >();

  data.forEach((point) => {
    // Round down to start of day
    const dayStart = Math.floor(point.orderedAt / dayMs) * dayMs;
    const existing = dailyMap.get(dayStart);
    if (existing) {
      existing.returned += point.returned;
      existing.ordered += point.ordered;
      existing.countryOrdered += point.countryOrdered ?? 0;
    } else {
      dailyMap.set(dayStart, {
        returned: point.returned,
        ordered: point.ordered,
        countryOrdered: point.countryOrdered ?? 0,
      });
    }
  });

  return Array.from(dailyMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([orderedAt, { returned, ordered, countryOrdered }]) => {
      const returnRate = ordered > 0 ? (returned / ordered) * 100 : 0;
      const countryReturnRate =
        countryOrdered > 0 ? (returned / countryOrdered) * 100 : undefined;
      return {
        orderedAt,
        returnRate: Math.round(returnRate * 100) / 100,
        returned,
        ordered,
        countryOrdered: countryOrdered || undefined,
        countryReturnRate:
          countryReturnRate !== undefined
            ? Math.round(countryReturnRate * 100) / 100
            : undefined,
      };
    });
}

type ChartDataPoint = {
  orderedAt: number;
  returnRate: number;
  returned: number;
  ordered: number;
  // 30-day window totals (only present when smoothing is applied)
  windowReturned?: number;
  windowOrdered?: number;
  // Country-specific return rate (returned / countryOrdered, only when country filter is active)
  countryReturnRate?: number;
  countryOrdered?: number;
  countryWindowReturned?: number;
  countryWindowOrdered?: number;
};

function calculateMovingAverage(
  data: {
    orderedAt: number;
    returned: number;
    ordered: number;
    countryOrdered?: number;
  }[],
  windowDays: number,
): ChartDataPoint[] {
  // First aggregate by day for cleaner moving average calculation
  const dailyData = aggregateByDay(data);
  const windowMs = windowDays * 24 * 60 * 60 * 1000;
  const hasCountryData = dailyData.some((p) => p.countryOrdered !== undefined);

  return dailyData.map((point, index) => {
    const windowStart = point.orderedAt - windowMs;
    let totalReturned = 0;
    let totalOrdered = 0;
    let totalCountryOrdered = 0;

    for (let i = 0; i <= index; i++) {
      if (
        dailyData[i].orderedAt >= windowStart &&
        dailyData[i].orderedAt <= point.orderedAt
      ) {
        totalReturned += dailyData[i].returned;
        totalOrdered += dailyData[i].ordered;
        totalCountryOrdered += dailyData[i].countryOrdered ?? 0;
      }
    }

    const returnRate =
      totalOrdered > 0 ? (totalReturned / totalOrdered) * 100 : 0;
    const countryReturnRate =
      hasCountryData && totalCountryOrdered > 0
        ? (totalReturned / totalCountryOrdered) * 100
        : undefined;

    return {
      orderedAt: point.orderedAt,
      returnRate: Math.round(returnRate * 100) / 100,
      returned: point.returned,
      ordered: point.ordered,
      windowReturned: totalReturned,
      windowOrdered: totalOrdered,
      countryReturnRate:
        countryReturnRate !== undefined
          ? Math.round(countryReturnRate * 100) / 100
          : undefined,
      countryOrdered: point.countryOrdered,
      countryWindowReturned: hasCountryData ? totalReturned : undefined,
      countryWindowOrdered: hasCountryData ? totalCountryOrdered : undefined,
    };
  });
}

export function ReturnRateChart() {
  const t = useTranslation();
  const {
    shop,
    dateRange,
    filters,
    productFilter,
    includeAllOrders,
    chartSmoothing,
    setChartSmoothing,
  } = useAnalyticsFilters();

  const resolution = filters.get("resolution")?.value.join(",");
  const reason = filters.get("reason")?.value.join(",");
  const country = filters.get("country")?.value.join(",");
  const sourceChannel = filters.get("sourceChannel")?.value.join(",");
  const productTags = filters.get("productTag")?.value.join(",");

  // Always load 30 days of extra data for smoothing calculation
  // This prevents reloading when switching between smoothing modes
  const extendedFromTimestamp = useMemo(() => {
    const smoothingMs = 30 * 24 * 60 * 60 * 1000;
    return dateRange.from - smoothingMs;
  }, [dateRange.from]);

  const [locale] = useLocale();
  const hasCountryFilter = !!country;
  const countryLabel = hasCountryFilter
    ? formatCountryList(filters.get("country")?.value ?? [], locale)
    : undefined;
  const countryName = countryLabel?.short;

  const [showCountryCurve, setShowCountryCurve] = useState(true);
  const [showGlobalCurve, setShowGlobalCurve] = useState(true);

  const toggleCountryCurve = () => {
    if (showCountryCurve && !showGlobalCurve) return;
    setShowCountryCurve((v) => !v);
  };

  const toggleGlobalCurve = () => {
    if (showGlobalCurve && !showCountryCurve) return;
    setShowGlobalCurve((v) => !v);
  };

  const {
    data: timeChartData,
    isLoading,
    isFetching,
  } = useReturnRequestsRatesTimeChart(shop.id, {
    fromTimestamp: extendedFromTimestamp,
    toTimestamp: dateRange.to,
    // The min-orders threshold must count the displayed period, not the preload.
    displayFromTimestamp: dateRange.from,
    ...(resolution && { resolution }),
    ...(reason && { reason }),
    ...(country && { country }),
    ...(sourceChannel && { sourceChannel }),
    ...(productTags && { productTags }),
    ...(productFilter?.catalogId && { catalogId: productFilter.catalogId }),
    ...(productFilter?.variantId && { variantId: productFilter.variantId }),
    ...(includeAllOrders && { includeAllOrders: true }),
  });

  const showLoader = useDebouncedLoading(isFetching);

  const chartConfig = {
    returnRate: {
      label: hasCountryFilter
        ? t("analytics.allCountries")
        : t("analytics.returnRate"),
      color: RETURN_RATE_COLORS[1], // Yellow as middle color for legend
    },
    ...(hasCountryFilter &&
      countryName && {
        countryReturnRate: {
          label: countryName,
          color: "hsl(var(--chart-1))",
        },
      }),
  } satisfies ChartConfig;

  const chartData = useMemo(() => {
    if (!timeChartData) return [];

    const sortedData = [...timeChartData].sort(
      (a, b) => a.orderedAt - b.orderedAt,
    );

    let processedData: ChartDataPoint[];
    if (chartSmoothing === "instant") {
      processedData = aggregateByDay(sortedData);
    } else {
      processedData = calculateMovingAverage(sortedData, 30);
    }

    // Filter to only show data within the original date range
    // (pre-loaded data was only used for smoothing calculation)
    const filteredData = processedData.filter(
      (item) =>
        item.orderedAt >= dateRange.from && item.orderedAt <= dateRange.to,
    );

    // Extend the chart to the end of the period with the last known rate
    if (filteredData.length > 0) {
      const lastPoint = filteredData[filteredData.length - 1];
      if (lastPoint.orderedAt < dateRange.to) {
        filteredData.push({
          ...lastPoint,
          orderedAt: dateRange.to,
        });
      }
    }

    return filteredData;
  }, [timeChartData, chartSmoothing, dateRange.from, dateRange.to]);

  const totalStats = useMemo(() => {
    if (!timeChartData)
      return {
        returned: 0,
        ordered: 0,
        rate: 0,
        countryOrdered: 0,
        countryRate: 0,
      };

    // Only count data within the original date range (not pre-loaded data)
    const totals = timeChartData
      .filter(
        (item) =>
          item.orderedAt >= dateRange.from && item.orderedAt <= dateRange.to,
      )
      .reduce(
        (acc, item) => {
          acc.returned += item.returned;
          acc.ordered += item.ordered;
          acc.countryOrdered +=
            (item as { countryOrdered?: number }).countryOrdered ?? 0;
          return acc;
        },
        { returned: 0, ordered: 0, countryOrdered: 0 },
      );

    return {
      ...totals,
      rate: totals.ordered > 0 ? (totals.returned / totals.ordered) * 100 : 0,
      countryRate:
        totals.countryOrdered > 0
          ? (totals.returned / totals.countryOrdered) * 100
          : 0,
    };
  }, [timeChartData, dateRange.from, dateRange.to]);

  const computeAxisConfig = (
    values: number[],
    minMax = 5,
  ): { domain: [number, number]; ticks: number[] } => {
    const maxRate = values.length > 0 ? Math.max(...values) : 0;

    let max: number;
    if (maxRate <= 5) max = 5;
    else if (maxRate <= 10) max = 10;
    else max = Math.ceil(maxRate / 10) * 10;
    max = Math.min(100, Math.max(minMax, max));

    let step: number;
    if (max <= 5) step = 1;
    else if (max <= 10) step = 2;
    else if (max <= 30) step = 5;
    else step = 10;

    // Ensure max is divisible by step for clean ticks
    if (max % step !== 0) max = Math.min(100, Math.ceil(max / step) * step);

    const ticks: number[] = [];
    for (let v = 0; v <= max; v += step) ticks.push(v);
    return { domain: [0, max], ticks };
  };

  const defaultMax = RETURN_RATE_THRESHOLDS[RETURN_RATE_THRESHOLDS.length - 1];

  // Left axis = primary curve with gradient (country when filter active, global otherwise)
  const leftAxisConfig = useMemo(() => {
    const values = hasCountryFilter
      ? chartData.map((d) => d.countryReturnRate ?? 0)
      : chartData.map((d) => d.returnRate);
    return computeAxisConfig(values, defaultMax);
  }, [chartData, hasCountryFilter]);

  // Right axis = global dashed curve, tight fit to its data range
  const rightAxisConfig = useMemo(() => {
    return computeAxisConfig(chartData.map((d) => d.returnRate));
  }, [chartData]);

  // Generate ticks based on full date range, not just data points
  const axisTicks = useMemo(() => {
    const dayMs = 24 * 60 * 60 * 1000;
    const rangeDays = Math.ceil((dateRange.to - dateRange.from) / dayMs);

    // Determine tick interval based on range
    let tickIntervalDays: number;
    if (rangeDays <= 14) tickIntervalDays = 1;
    else if (rangeDays <= 31) tickIntervalDays = 3;
    else if (rangeDays <= 90) tickIntervalDays = 7;
    else tickIntervalDays = 14;

    // Generate ticks from start to end of date range
    const ticks: number[] = [];
    let currentTick = dateRange.from;
    while (currentTick <= dateRange.to) {
      ticks.push(currentTick);
      currentTick += tickIntervalDays * dayMs;
    }
    // Always include the end date if not already included
    if (ticks[ticks.length - 1] < dateRange.to) {
      ticks.push(dateRange.to);
    }
    return ticks;
  }, [dateRange.from, dateRange.to]);

  const fromDate = format(dateRange.from, "d MMM yyyy");
  const toDate = format(dateRange.to, "d MMM yyyy");

  return (
    <Card className="col-span-2">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-md">
              {t("analytics.returnRateEvolution")}
            </CardTitle>
            <CardDescription>
              {fromDate} - {toDate}
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <ToggleGroup
              type="single"
              value={chartSmoothing}
              onValueChange={(value) => {
                if (value) setChartSmoothing(value as ChartSmoothing);
              }}
              className="border rounded-md"
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem
                    value="instant"
                    size="sm"
                    className="text-xs px-3"
                  >
                    {t("analytics.smoothing.instant")}
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent className="max-w-64 space-y-2 py-3">
                  <p className="font-medium">
                    {t("analytics.smoothing.instantTooltip")}
                  </p>
                  <p className="font-mono text-xs opacity-80">
                    {t("analytics.smoothing.instantFormula")}
                  </p>
                  <ul className="list-disc pl-4 space-y-1 text-xs opacity-80">
                    <li>{t("analytics.smoothing.instantBehavior")}</li>
                    <li>{t("analytics.smoothing.scopeNote")}</li>
                  </ul>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem
                    value="30d"
                    size="sm"
                    className="text-xs px-3"
                  >
                    {t("analytics.smoothing.30days")}
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent className="max-w-64 space-y-2 py-3">
                  <p className="font-medium">
                    {t("analytics.smoothing.30daysTooltip")}
                  </p>
                  <p className="font-mono text-xs opacity-80">
                    {t("analytics.smoothing.30daysFormula")}
                  </p>
                  <ul className="list-disc pl-4 space-y-1 text-xs opacity-80">
                    <li>{t("analytics.smoothing.30daysBehavior")}</li>
                    <li>{t("analytics.smoothing.scopeNote")}</li>
                  </ul>
                </TooltipContent>
              </Tooltip>
            </ToggleGroup>
            {hasCountryFilter ? (
              <div className="grid grid-cols-2 gap-4 text-right">
                {/* Country column */}
                <button
                  type="button"
                  onClick={toggleCountryCurve}
                  className={`min-w-[120px] text-right cursor-pointer transition-opacity ${!showCountryCurve ? "opacity-30" : ""}`}
                >
                  <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
                    <Tooltip>
                      <TooltipTrigger
                        asChild
                        onClick={(e) => e.stopPropagation()}
                      >
                        <InfoCircledIcon className="h-3 w-3 shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-64 space-y-2 py-3">
                        <p className="font-medium">
                          {t("analytics.returnRateTooltip.countryTitle")}
                        </p>
                        <p className="font-mono text-xs opacity-80">
                          {t("analytics.returnRateTooltip.countryFormula")}
                        </p>
                        <ul className="list-disc pl-4 space-y-1 text-xs opacity-80">
                          <li>
                            {t("analytics.returnRateTooltip.countryNote")}
                          </li>
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                    <span title={countryLabel?.full}>{countryName}</span>
                  </div>
                  <div className="text-2xl font-bold tabular-nums">
                    {totalStats.countryRate.toFixed(1)}%
                  </div>
                  <div className="text-xs text-muted-foreground tabular-nums">
                    {totalStats.returned} / {totalStats.countryOrdered}
                  </div>
                </button>
                {/* Global column */}
                <button
                  type="button"
                  onClick={toggleGlobalCurve}
                  className={`min-w-[120px] text-right cursor-pointer transition-opacity ${!showGlobalCurve ? "opacity-30" : ""}`}
                >
                  <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
                    <Tooltip>
                      <TooltipTrigger
                        asChild
                        onClick={(e) => e.stopPropagation()}
                      >
                        <InfoCircledIcon className="h-3 w-3 shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-64 space-y-2 py-3">
                        <p className="font-medium">
                          {t("analytics.returnRateTooltip.globalTitle")}
                        </p>
                        <p className="font-mono text-xs opacity-80">
                          {t("analytics.returnRateTooltip.globalFormula")}
                        </p>
                        <ul className="list-disc pl-4 space-y-1 text-xs opacity-80">
                          <li>{t("analytics.returnRateTooltip.globalNote")}</li>
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                    <span>{t("analytics.allCountries")}</span>
                  </div>
                  <div className="text-2xl font-bold text-muted-foreground tabular-nums">
                    {totalStats.rate.toFixed(1)}%
                  </div>
                  <div className="text-xs text-muted-foreground tabular-nums">
                    {totalStats.returned} / {totalStats.ordered}
                  </div>
                </button>
              </div>
            ) : (
              <div className="min-w-[120px] text-right">
                <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InfoCircledIcon className="h-3 w-3 shrink-0 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-64 space-y-2 py-3">
                      <p className="font-medium">
                        {t("analytics.returnRateTooltip.globalTitle")}
                      </p>
                      <p className="font-mono text-xs opacity-80">
                        {t("analytics.returnRateTooltip.globalFormula")}
                      </p>
                      <ul className="list-disc pl-4 space-y-1 text-xs opacity-80">
                        <li>{t("analytics.returnRateTooltip.globalNote")}</li>
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                  <span>{t("analytics.returnRate")}</span>
                </div>
                <div className="text-2xl font-bold tabular-nums">
                  {totalStats.rate.toFixed(1)}%
                </div>
                <div className="text-xs text-muted-foreground tabular-nums">
                  {totalStats.returned} / {totalStats.ordered}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="relative h-[214px]">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-pulse text-muted-foreground">
              {t("misc.loading")}
            </div>
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-full w-full">
            <AreaChart data={chartData} margin={{ left: 0, right: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} yAxisId="left" />
              <XAxis
                dataKey="orderedAt"
                type="number"
                scale="time"
                domain={[dateRange.from, dateRange.to]}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value: number) => {
                  if (!value) return "";
                  return format(new Date(value), "d MMM");
                }}
                ticks={axisTicks}
              />
              <YAxis
                yAxisId="left"
                width={45}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => `${value}%`}
                domain={leftAxisConfig.domain}
                ticks={leftAxisConfig.ticks}
                style={{
                  opacity: hasCountryFilter && !showCountryCurve ? 0 : 1,
                }}
              />
              {hasCountryFilter && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  width={45}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(value) => `${value}%`}
                  domain={rightAxisConfig.domain}
                  ticks={rightAxisConfig.ticks}
                  style={{ opacity: showGlobalCurve ? 1 : 0 }}
                />
              )}
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(_, payload) => {
                      if (payload?.[0]?.payload?.orderedAt) {
                        return format(
                          new Date(payload[0].payload.orderedAt),
                          "d MMM yyyy",
                        );
                      }
                      return "";
                    }}
                    formatter={(value, name, item) => {
                      if (name === "returnRate") {
                        const hasWindowData =
                          item.payload.windowReturned !== undefined;
                        const label = hasCountryFilter
                          ? t("analytics.allCountries")
                          : t("analytics.returnRate");
                        return (
                          <div className="flex w-full flex-col gap-0.5">
                            <div className="flex w-full items-baseline justify-between gap-4">
                              <span className="text-xs text-muted-foreground">
                                {label}
                              </span>
                              <span className="ml-auto font-semibold tabular-nums">
                                {value}%
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {hasWindowData ? (
                                <>
                                  {item.payload.windowReturned} /{" "}
                                  {item.payload.windowOrdered}
                                </>
                              ) : (
                                <>
                                  {item.payload.returned} /{" "}
                                  {item.payload.ordered}
                                </>
                              )}
                            </span>
                          </div>
                        );
                      }
                      if (name === "countryReturnRate") {
                        const hasWindowData =
                          item.payload.countryWindowReturned !== undefined;
                        return (
                          <div className="flex w-full flex-col gap-0.5">
                            <div className="flex w-full items-baseline justify-between gap-4">
                              <span className="text-xs text-muted-foreground">
                                {countryLabel?.full}
                              </span>
                              <span className="ml-auto font-semibold tabular-nums">
                                {value}%
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {hasWindowData ? (
                                <>
                                  {item.payload.countryWindowReturned} /{" "}
                                  {item.payload.countryWindowOrdered}
                                </>
                              ) : (
                                <>
                                  {item.payload.returned} /{" "}
                                  {item.payload.countryOrdered}
                                </>
                              )}
                            </span>
                          </div>
                        );
                      }
                      return value;
                    }}
                  />
                }
              />
              <defs>
                {/* Vertical gradient for fill: green at bottom (0%) to red at top (35%)
                    Using userSpaceOnUse to map gradient to full chart area, not just data bounds.
                    Chart is h-[214px], with ~40px for X-axis, so chart area is ~10px to ~174px */}
                <linearGradient
                  id="fillReturnRate"
                  gradientUnits="userSpaceOnUse"
                  x1="0"
                  y1="174"
                  x2="0"
                  y2="10"
                >
                  <stop
                    offset="0%"
                    stopColor={RETURN_RATE_COLORS[0]}
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="28%"
                    stopColor={RETURN_RATE_COLORS[1]}
                    stopOpacity={0.4}
                  />
                  <stop
                    offset="57%"
                    stopColor={RETURN_RATE_COLORS[2]}
                    stopOpacity={0.5}
                  />
                  <stop
                    offset="100%"
                    stopColor={RETURN_RATE_COLORS[3]}
                    stopOpacity={0.6}
                  />
                </linearGradient>
                {/* Vertical gradient for stroke */}
                <linearGradient
                  id="strokeReturnRate"
                  gradientUnits="userSpaceOnUse"
                  x1="0"
                  y1="174"
                  x2="0"
                  y2="10"
                >
                  <stop offset="0%" stopColor={RETURN_RATE_COLORS[0]} />
                  <stop offset="28%" stopColor={RETURN_RATE_COLORS[1]} />
                  <stop offset="57%" stopColor={RETURN_RATE_COLORS[2]} />
                  <stop offset="100%" stopColor={RETURN_RATE_COLORS[3]} />
                </linearGradient>
              </defs>
              {hasCountryFilter && (
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="countryReturnRate"
                  stroke={
                    showCountryCurve ? "url(#strokeReturnRate)" : "transparent"
                  }
                  strokeWidth={2}
                  fill={
                    showCountryCurve ? "url(#fillReturnRate)" : "transparent"
                  }
                  isAnimationActive={false}
                  connectNulls
                />
              )}
              <Area
                yAxisId={hasCountryFilter ? "right" : "left"}
                type="monotone"
                dataKey="returnRate"
                stroke={
                  !hasCountryFilter
                    ? "url(#strokeReturnRate)"
                    : showGlobalCurve
                      ? "var(--muted-foreground)"
                      : "transparent"
                }
                opacity={hasCountryFilter && showGlobalCurve ? 0.5 : 1}
                strokeWidth={hasCountryFilter ? 1.5 : 2}
                strokeDasharray={hasCountryFilter ? "6 4" : undefined}
                fill={
                  !hasCountryFilter ? "url(#fillReturnRate)" : "transparent"
                }
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            </AreaChart>
          </ChartContainer>
        )}
        {showLoader && !isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-md">
            <div className="animate-pulse text-muted-foreground text-sm">
              {t("misc.loading")}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
